import { useState, useEffect } from "react"
import { Button, Input } from "@aix/ui"
import { trpc } from "~/lib/trpc"
import toast from "react-hot-toast"
import CategorySection from "./components/CategorySection"

export default function CalendarTab() {
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split("T")[0])
  const [entries, setEntries] = useState<Record<string, any>>({})
  const [hasChanges, setHasChanges] = useState(false)

  // Queries
  const { data: trackables } = trpc.trackables.getAllTrackables.useQuery()
  const { data: existingEntries, refetch: refetchEntries } = trpc.trackables.getEntriesByDate.useQuery({
    date: selectedDate,
  })

  const { data: statsData } = trpc.trackables.getEntriesStats.useQuery({
    dateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
    dateTo: new Date().toISOString().split("T")[0],
  })

  // Mutations
  const submitMutation = trpc.trackables.submitCheckIn.useMutation({
    onSuccess: () => {
      toast.success("Entries saved successfully!")
      setHasChanges(false)
      void refetchEntries()
    },
    onError: (error) => {
      toast.error(`Failed to save: ${error.message}`)
    },
  })

  const utils = trpc.useUtils()

  // Load existing entries when date changes
  useEffect(() => {
    if (existingEntries) {
      const entriesMap: Record<string, any> = {}
      existingEntries.entries?.forEach((entry: any) => {
        if (entry.numericValue !== null) {
          entriesMap[entry.trackableId] = parseFloat(entry.numericValue)
        } else if (entry.booleanValue !== null) {
          entriesMap[entry.trackableId] = entry.booleanValue
        } else if (entry.textValue !== null) {
          entriesMap[entry.trackableId] = entry.textValue
        }
      })
      setEntries(entriesMap)
      setHasChanges(false)
    }
  }, [existingEntries, selectedDate])

  const handleEntryChange = (trackableId: string, value: any) => {
    setEntries((prev) => ({
      ...prev,
      [trackableId]: value,
    }))
    setHasChanges(true)
  }

  const handleSaveAll = () => {
    const entriesArray = Object.entries(entries)
      .filter(([_, value]) => value !== undefined && value !== null && value !== "")
      .map(([trackableId, value]) => ({
        trackableId,
        value,
      }))

    if (entriesArray.length === 0) {
      toast.error("No entries to save")
      return
    }

    submitMutation.mutate({
      date: selectedDate,
      window: "evening", // Default to evening for now
      entries: entriesArray,
    })
  }

  // Generate last 30 days for calendar
  const getLast30Days = () => {
    const days = []
    for (let i = 29; i >= 0; i--) {
      const date = new Date()
      date.setDate(date.getDate() - i)
      days.push(date.toISOString().split("T")[0])
    }
    return days
  }

  const getCompletionForDate = (date: string) => {
    const completion = statsData?.completionRates?.find((c: any) => c.date === date)
    return completion
  }

  const getCompletionColor = (percentage: number) => {
    if (percentage === 0) return "bg-gray-300"
    if (percentage < 50) return "bg-yellow-500"
    if (percentage < 100) return "bg-yellow-500"
    return "bg-green-500"
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    return {
      day: days[date.getDay()],
      date: date.getDate(),
      month: months[date.getMonth()],
    }
  }

  const today = new Date().toISOString().split("T")[0]
  const last30Days = getLast30Days()

  // Group trackables by category and window
  const morningTrackables = trackables?.filter((t) => t.checkInWindow === "morning" && t.isActive)
  const eveningTrackables = trackables?.filter((t) => t.checkInWindow === "evening" && t.isActive)

  const groupByCategory = (items: any[]) => {
    const groups: Record<string, any[]> = {}
    items?.forEach((item) => {
      if (!groups[item.category]) {
        groups[item.category] = []
      }
      groups[item.category].push(item)
    })
    return groups
  }

  const morningByCategory = groupByCategory(morningTrackables || [])
  const eveningByCategory = groupByCategory(eveningTrackables || [])

  return (
    <div className="flex h-full">
      {/* Left Sidebar: Calendar */}
      <div className="w-80 border-r-4 border-border bg-secondary p-4 overflow-y-auto">
        <h3 className="text-lg font-bold text-primary mb-4">📅 Select Date</h3>
        <div className="space-y-2">
          {last30Days.map((date) => {
            const formatted = formatDate(date)
            const completion = getCompletionForDate(date)
            const isToday = date === today
            const isSelected = date === selectedDate

            return (
              <button
                key={date}
                onClick={() => setSelectedDate(date)}
                className={`w-full border-2 p-3 text-left transition-all ${
                  isToday
                    ? "border-red-500"
                    : isSelected
                      ? "border-primary bg-yellow-200"
                      : "border-border hover:bg-background"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-muted-foreground">
                      {formatted.day}, {formatted.month} {formatted.date}
                    </div>
                    <div className="text-sm font-semibold">{date}</div>
                  </div>
                  {completion && (
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${getCompletionColor(completion.percentage)}`} />
                      <span className="text-xs">{Math.round(completion.percentage)}%</span>
                    </div>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Right Content: Entry Form */}
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-primary">
                Entries for {formatDate(selectedDate).day}, {formatDate(selectedDate).month}{" "}
                {formatDate(selectedDate).date}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">Fill in your trackables for this date</p>
            </div>
            <div className="flex items-center gap-2">
              {hasChanges && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
              <Button
                onClick={handleSaveAll}
                loading={submitMutation.isLoading}
                disabled={!hasChanges}
                className="border-2 border-border"
              >
                💾 Save All
              </Button>
            </div>
          </div>

          {/* Morning Section */}
          {morningTrackables && morningTrackables.length > 0 && (
            <div className="bg-secondary border-4 border-border p-6 shadow-lg">
              <h3 className="text-xl font-bold text-primary mb-4">🌅 Morning Trackables</h3>
              <div className="space-y-4">
                {Object.entries(morningByCategory).map(([category, items]) => (
                  <CategorySection
                    key={category}
                    category={category as any}
                    trackables={items}
                    entries={entries}
                    onEntryChange={handleEntryChange}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Evening Section */}
          {eveningTrackables && eveningTrackables.length > 0 && (
            <div className="bg-secondary border-4 border-border p-6 shadow-lg">
              <h3 className="text-xl font-bold text-primary mb-4">🌙 Evening Trackables</h3>
              <div className="space-y-4">
                {Object.entries(eveningByCategory).map(([category, items]) => (
                  <CategorySection
                    key={category}
                    category={category as any}
                    trackables={items}
                    entries={entries}
                    onEntryChange={handleEntryChange}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {(!trackables || trackables.length === 0) && (
            <div className="bg-secondary border-4 border-border p-8 text-center">
              <p className="text-muted-foreground mb-4">No trackables configured yet</p>
              <Button
                onClick={() => {
                  // Navigate to trackables tab
                  const event = new CustomEvent("navigate-to-tab", { detail: "trackables" })
                  window.dispatchEvent(event)
                }}
                className="border-2 border-border"
              >
                Create Your First Trackable
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
